import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { FAQSchema } from './SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function FAQSection({ category = null, title = "Frequently Asked Questions" }) {
  const [faqs, setFaqs] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/site-settings`);
        let allFaqs = response.data.faqs || [];
        
        // If no FAQs from database, use defaults
        if (allFaqs.length === 0) {
          allFaqs = defaultFAQs;
        }
        
        // Filter by category if specified
        if (category) {
          allFaqs = allFaqs.filter(faq => faq.category === category);
        }
        
        setFaqs(allFaqs);
      } catch (error) {
        console.error('Failed to fetch FAQs:', error);
        // Fallback to default FAQs
        setFaqs(defaultFAQs.filter(faq => !category || faq.category === category));
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, [category]);

  if (loading || faqs.length === 0) return null;

  return (
    <section className="section-padding bg-muted/30" data-testid="faq-section">
      <FAQSchema faqs={faqs} />
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">
            Have Questions?
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-foreground speakable-content">
            {title}
          </h2>
        </motion.div>

        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-card border border-border rounded-lg overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
                data-testid={`faq-toggle-${index}`}
              >
                <span className="font-medium text-foreground pr-4 speakable-content">
                  {faq.question}
                </span>
                <ChevronDown 
                  className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="px-6 pb-4 text-muted-foreground speakable-content">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Default FAQs as fallback
const defaultFAQs = [
  {
    question: "What is the check-in and check-out time?",
    answer: "Our standard check-in time is 2:00 PM and check-out time is 11:00 AM. Early check-in and late check-out can be arranged upon request, subject to availability.",
    category: "general"
  },
  {
    question: "How far is The Hotel Emerald from the nearest airport?",
    answer: "The nearest airport is Visakhapatnam (VIZAG), which is approximately 230 km away, about 5 hours by road. We can arrange airport transfers upon request.",
    category: "location"
  },
  {
    question: "What amenities are included in the room?",
    answer: "All our rooms include free WiFi, air conditioning, flat-screen TV, mini bar, tea/coffee maker, 24-hour room service, and premium toiletries. Suite categories include additional amenities like private balconies and living areas.",
    category: "rooms"
  },
  {
    question: "Does the hotel have a restaurant?",
    answer: "Yes, we have a multi-cuisine restaurant serving Indian, Continental, and local Odia specialties. Room service is available 24 hours a day.",
    category: "dining"
  },
  {
    question: "Is the hotel suitable for weddings and events?",
    answer: "Absolutely! The Hotel Emerald is a premier destination wedding venue with capacity for up to 500 guests. We offer comprehensive event planning, catering, and decoration services with stunning mountain backdrops.",
    category: "general"
  },
  {
    question: "What activities are available nearby?",
    answer: "Guests can enjoy trekking to Deomali Peak, visiting Duduma Waterfalls, exploring tribal markets, coffee plantation tours, boating at Kolab Reservoir, and visiting the historic Jagannath Temple in Koraput.",
    category: "location"
  },
  {
    question: "What is your cancellation policy?",
    answer: "Free cancellation is available up to 24 hours before check-in. Cancellations within 24 hours may be subject to one night's room charge. For peak season bookings and special events, please check specific terms.",
    category: "general"
  },
  {
    question: "Is parking available at the hotel?",
    answer: "Yes, we offer complimentary parking for all guests. Valet parking service is also available.",
    category: "amenities"
  }
];
